import { useEffect, useState } from "react";
import {
  FaArrowUpRightFromSquare,
  FaCodeBranch,
  FaGithub,
  FaInstagram,
  FaMoon,
  FaRegStar,
  FaSun,
  FaYoutube,
} from "react-icons/fa6";

import "./Home.css";

const GITHUB_USERNAME = "soleroks";

const featuredRepos = [
  "effective-enigma",
  "emiraktasdev",
  "soleroks",
];

const mediaLinks = [
  {
    eyebrow: "Instagram",
    title: "Instagram",
    description:
      "Photos, projects, and updates.",
    href: "https://www.instagram.com/emiraktas.dev/",
    icon: <FaInstagram />,
    className: "media-card--instagram",
  },
  {
    eyebrow: "YouTube",
    title: "YouTube",
    description:
      "Videos about coding and projects.",
    href: "https://www.youtube.com/@soleroks",
    icon: <FaYoutube />,
    className: "media-card--youtube",
  },
];

function useTheme() {
  const [theme, setTheme] = useState(() => {
    const savedTheme = window.localStorage.getItem("emir-theme");
    if (savedTheme) return savedTheme;
    return window.matchMedia("(prefers-color-scheme: light)").matches
      ? "light"
      : "dark";
  });

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem("emir-theme", theme);
  }, [theme]);

  return [theme, () => setTheme((current) => (current === "dark" ? "light" : "dark"))];
}

function RepoCard({ repo }) {
  return (
    <a
      className="repo-card"
      href={repo.html_url}
      target="_blank"
      rel="noreferrer"
    >
      <div className="repo-card__topline">
        <span className="repo-card__language">
          <span className="language-dot" />
          {repo.language || "code"}
        </span>
        <FaArrowUpRightFromSquare aria-hidden="true" />
      </div>
      <h3>{repo.name.replaceAll("-", " ")}</h3>
      <p>{repo.description || "A focused experiment from the workshop."}</p>
      <div className="repo-card__meta">
        <span>
          <FaRegStar aria-hidden="true" /> {repo.stargazers_count}
        </span>
        <span>
          <FaCodeBranch aria-hidden="true" /> {repo.forks_count}
        </span>
      </div>
    </a>
  );
}

function Repositories() {
  const [repos, setRepos] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    const controller = new AbortController();

    fetch(
      `https://api.github.com/users/${GITHUB_USERNAME}/repos?sort=updated&per_page=12`,
      { signal: controller.signal },
    )
      .then((response) => {
        if (!response.ok) throw new Error("Unable to load repositories");
        return response.json();
      })
      .then((data) => {
        const sortedRepos = data
          .filter((repo) => !repo.fork)
          .sort((a, b) => {
            const aFeatured = featuredRepos.includes(a.name) ? 1 : 0;
            const bFeatured = featuredRepos.includes(b.name) ? 1 : 0;
            return bFeatured - aFeatured || new Date(b.updated_at) - new Date(a.updated_at);
          })
          .slice(0, 6);
        setRepos(sortedRepos);
        setStatus("ready");
      })
      .catch((error) => {
        if (error.name !== "AbortError") setStatus("error");
      });

    return () => controller.abort();
  }, []);

  return (
    <section className="section section--repos" id="work">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Selected work</span>
          <h2>Things I&apos;ve been making.</h2>
        </div>
        <a
          className="text-link"
          href={`https://github.com/${GITHUB_USERNAME}?tab=repositories`}
          target="_blank"
          rel="noreferrer"
        >
          All repositories <FaArrowUpRightFromSquare aria-hidden="true" />
        </a>
      </div>

      {status === "loading" && (
        <div className="repo-grid" aria-label="Loading repositories">
          {[1, 2, 3].map((item) => (
            <div className="repo-card repo-card--skeleton" key={item} />
          ))}
        </div>
      )}

      {status === "ready" && repos.length > 0 && (
        <div className="repo-grid">
          {repos.map((repo) => (
            <RepoCard key={repo.id} repo={repo} />
          ))}
        </div>
      )}

      {status === "error" && (
        <div className="inline-message">
          GitHub is taking a moment. <a href={`https://github.com/${GITHUB_USERNAME}`}>Browse the profile instead.</a>
        </div>
      )}
    </section>
  );
}

function Home() {
  const [theme, toggleTheme] = useTheme();

  return (
    <main>
      <nav className="site-nav" aria-label="Main navigation">
        <a className="wordmark" href="/" aria-label="Emir home">
          emir<span>.</span>
        </a>
        <div className="nav-actions">
          <a href="#work">Work</a>
          <a href="#connect">Connect</a>
          <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle color theme">
            {theme === "dark" ? <FaSun aria-hidden="true" /> : <FaMoon aria-hidden="true" />}
          </button>
        </div>
      </nav>

      <section className="hero">
        <div className="hero__orb hero__orb--one" />
        <div className="hero__orb hero__orb--two" />
        <div className="hero__content">
          <div className="availability">
            <span className="availability__dot" />
            Available for work
          </div>
          <p className="hero__kicker">Developer and backend engineer</p>
          <h1>I build simple and useful software.</h1>
          <p className="hero__intro">
            I&apos;m Emir. I work with Node.js, React, Linux, and cloud tools.
          </p>
          <div className="hero__actions">
            <a className="button button--primary" href="#work">View my work</a>
            <a className="button button--quiet" href="mailto:hello@emiraktas.dev">Contact me <FaArrowUpRightFromSquare /></a>
          </div>
        </div>
        <div className="hero__scroll-note">Scroll to explore <span>↓</span></div>
      </section>

      <section className="section section--about">
        <div className="about__statement">
          <span className="eyebrow">A little context</span>
          <h2>I like software that is clear and reliable.</h2>
        </div>
        <div className="about__copy">
          <p>
            I build backend systems and interfaces. I care about readable code,
            fast websites, stable services, and good user experiences.
          </p>
          <div className="tool-list" aria-label="Technologies">
            <span>JavaScript</span><span>TypeScript</span><span>Node.js</span>
            <span>React</span><span>Linux</span><span>Docker</span>
          </div>
        </div>
      </section>

      <Repositories />

      <section className="section section--media" id="connect">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Social</span>
            <h2>Follow my work.</h2>
          </div>
          <p className="section-heading__note">Updates and videos.</p>
        </div>
        <div className="media-grid">
          {mediaLinks.map((media) => (
            <a
              className={`media-card ${media.className}`}
              href={media.href}
              target="_blank"
              rel="noreferrer"
              key={media.eyebrow}
            >
              <div className="media-card__icon">{media.icon}</div>
              <div>
                <span className="eyebrow">{media.eyebrow}</span>
                <h3>{media.title}</h3>
                <p>{media.description}</p>
              </div>
              <FaArrowUpRightFromSquare className="media-card__arrow" aria-hidden="true" />
            </a>
          ))}
        </div>
      </section>

      <footer className="site-footer">
        <span>© {new Date().getFullYear()} Emir Aktas</span>
        <div>
          <a href="https://github.com/soleroks" target="_blank" rel="noreferrer" aria-label="GitHub"><FaGithub /></a>
          <a href="https://www.instagram.com/emiraktas.dev/" target="_blank" rel="noreferrer" aria-label="Instagram"><FaInstagram /></a>
          <a href="https://www.youtube.com/@soleroks" target="_blank" rel="noreferrer" aria-label="YouTube"><FaYoutube /></a>
        </div>
        <span className="footer-note">Thanks for visiting.</span>
      </footer>
    </main>
  );
}

export default Home;
